"use client";

import { Box, Container } from "@mui/material";
import React, { useMemo } from "react";
import { SalifyHeaderLogoWrapper } from "./common/logo-wrapper";
import { CartButtonWithSwipeableDrawer } from "./default";
import { headerMenuToNavItems, parseHeaderMenu } from "./header-menu-utils";
import {
  DefaultV2DesktopHotlineBlock,
  DefaultV2DesktopNavLinks,
  DefaultV2ResponsiveHeaderNav,
} from "./default-v2/components";

interface VariantProps {
  logoUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
  name: string | undefined;
  menu?: string;
  hotline?: string;
  /** @default "/product" */
  productUrlPrefix?: string;
  /** @default "/checkout" */
  checkoutUrl?: string;
  /** @default "/cart" */
  cartUrl?: string;
}

const HeaderDefaultV2: React.FC<VariantProps> = (props) => {
  const navItems = useMemo(() => headerMenuToNavItems(parseHeaderMenu(props.menu)), [props.menu]);

  return (
    <Box
      sx={(theme) => ({
        bgcolor: "background.paper",
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
        overflow: "visible",
      })}
    >
      <Box sx={{ position: "relative" }}>
        <Container
          disableGutters
          sx={{
            display: { xs: "flex", laptop: "grid" },
            flexDirection: "row",
            justifyContent: { xs: "flex-start", laptop: "stretch" },
            alignItems: { xs: "center", laptop: "stretch" },
            gridTemplateColumns: { laptop: "auto minmax(0, 1fr) auto" },
            columnGap: { laptop: 1 },
            minHeight: { xs: 60, laptop: 72 },
            py: { xs: 0.5, laptop: 0.75 },
            paddingX: 0,
            overflow: "visible",
            pr: navItems.length ? { xs: 12, laptop: 0 } : 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, minWidth: 0 }}>
            <SalifyHeaderLogoWrapper
              logoUrl={props.logoUrl}
              logoWidth={props.logoWidth}
              logoHeight={props.logoHeight}
            />
          </Box>

          {!!navItems.length && (
            <Box
              sx={{
                display: { xs: "none", laptop: "flex" },
                justifyContent: "center",
                alignItems: "stretch",
                minWidth: 0,
              }}
            >
              <DefaultV2DesktopNavLinks items={navItems} />
            </Box>
          )}

          <Box
            sx={{
              display: { xs: "none", laptop: "flex" },
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
            }}
          >
            <CartButtonWithSwipeableDrawer
              cartUrl={props.cartUrl}
              checkoutUrl={props.checkoutUrl}
              productUrlPrefix={props.productUrlPrefix}
            />
            {props.hotline && (
              <Box
                sx={{
                  borderLeft: "1px solid",
                  borderColor: "grey.400",
                  pl: 2,
                }}
              >
                <DefaultV2DesktopHotlineBlock hotline={props.hotline} />
              </Box>
            )}
          </Box>
        </Container>

        {!!navItems.length && (
          <Box
            component="nav"
            aria-label="Menu điều hướng"
            sx={{
              display: { xs: "flex", laptop: "none" },
              position: "absolute",
              right: 4,
              top: 0,
              bottom: 0,
              alignItems: "center",
              zIndex: 50,
              pointerEvents: "auto",
              touchAction: "manipulation",
            }}
          >
            <DefaultV2ResponsiveHeaderNav
              items={navItems}
              hotline={props.hotline}
              cartUrl={props.cartUrl}
              checkoutUrl={props.checkoutUrl}
              productUrlPrefix={props.productUrlPrefix}
            />
          </Box>
        )}

        {!navItems.length && (
          <Box
            component="nav"
            aria-label="Menu điều hướng"
            sx={{
              display: { xs: "flex", laptop: "none" },
              position: "absolute",
              right: 4,
              top: 0,
              bottom: 0,
              alignItems: "center",
              zIndex: 50,
            }}
          >
            <DefaultV2ResponsiveHeaderNav
              items={[]}
              hotline={props.hotline}
              cartUrl={props.cartUrl}
              checkoutUrl={props.checkoutUrl}
              productUrlPrefix={props.productUrlPrefix}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

HeaderDefaultV2.displayName = "HeaderDefaultV2";

export default HeaderDefaultV2;
